export interface TagChipPlacement {
  text: string;
  icon: TagBadgeIcon | null;
  x: number;
  width: number;
  variant: 'tag' | 'overflow';
}

export interface TagBadgeIconLayer {
  path: string;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  fillRule?: 'nonzero' | 'evenodd';
}

export interface TagBadgeIcon {
  layers: ReadonlyArray<TagBadgeIconLayer>;
}

export interface TagBadgeLayout {
  chips: TagChipPlacement[];
  width: number;
  height: number;
  offsetY: number;
}

export const TAG_BADGE_MAX_VISIBLE = 3;
export const TAG_BADGE_CHAR_PX = 6.4;
export const TAG_BADGE_PAD_X = 8;
export const TAG_BADGE_HEIGHT = 16;
export const TAG_BADGE_GAP = 4;
export const TAG_BADGE_GLYPH_WIDTH = 12;
export const TAG_BADGE_GLYPH_GAP = 2;
export const TAG_BADGE_GRC_ROW_HEIGHT = 20;
export const TAG_BADGE_SHAPE_GAP = 4;

// One deterministic, emoji-like vector palette is shared by the Konva canvas
// and every SVG-backed export. Keeping the geometry and paint here prevents
// platform emoji/font differences from changing icons between surfaces.
const TAG_ICONS: Readonly<Record<string, TagBadgeIcon>> = {
  timed: {
    layers: [
      { path: 'M9 2h6v2H9z', fill: '#6f8fa4' },
      { path: 'M17.2 4.5l2.3 2.3', stroke: '#6f8fa4', strokeWidth: 2 },
      { path: 'M12 5a8 8 0 1 0 8 8 8 8 0 0 0-8-8z', fill: '#dceaf2', stroke: '#567b93', strokeWidth: 1.75 },
      { path: 'M12 8v5l3.2 2', stroke: '#476f89', strokeWidth: 2 },
    ],
  },
  manual: {
    layers: [
      { path: 'M12 3.25a4.25 4.25 0 1 0 0 8.5 4.25 4.25 0 0 0 0-8.5z', fill: '#4f8fba' },
      { path: 'M3.75 21c.45-4.65 3.2-7.25 8.25-7.25s7.8 2.6 8.25 7.25z', fill: '#4f8fba' },
    ],
  },
  automated: {
    layers: [
      {
        path: 'M10.1 2h3.8l.55 2.25c.55.2 1.08.5 1.55.9l2.2-.7 1.9 3.3-1.7 1.55c.1.55.1 1.15 0 1.7l1.7 1.55-1.9 3.3-2.2-.7c-.47.4-1 .7-1.55.9L13.9 18h-3.8l-.55-1.95a7.2 7.2 0 0 1-1.55-.9l-2.2.7-1.9-3.3L5.6 11a6.5 6.5 0 0 1 0-1.7L3.9 7.75l1.9-3.3 2.2.7c.47-.4 1-.7 1.55-.9zM12 7.25a2.9 2.9 0 1 0 0 5.8 2.9 2.9 0 0 0 0-5.8z',
        fill: '#778b9a',
        fillRule: 'evenodd',
      },
    ],
  },
  deliverable: {
    layers: [
      { path: 'M6 2.5h8l4 4V21.5H6z', fill: '#f8fbfd', stroke: '#6f8797', strokeWidth: 1.5 },
      { path: 'M14 2.5v4h4', fill: '#b9d4e5', stroke: '#6f8797', strokeWidth: 1.5 },
      { path: 'M9 11h6M9 14.5h6M9 18h4.5', stroke: '#5d7f95', strokeWidth: 1.5 },
    ],
  },
  risk: {
    layers: [
      { path: 'M12 2.75 22 20.5H2z', fill: '#f5c451', stroke: '#b7791f', strokeWidth: 1.4 },
      { path: 'M12 8v6.2', stroke: '#704b16', strokeWidth: 2.2 },
      { path: 'M12 17.3h.01', stroke: '#704b16', strokeWidth: 2.8 },
    ],
  },
  recurring: {
    layers: [
      { path: 'M19.5 7.5V3.8l-1.9 1.9A8 8 0 0 0 4.2 9', stroke: '#587f99', strokeWidth: 2 },
      { path: 'M4.5 16.5v3.7l1.9-1.9A8 8 0 0 0 19.8 15', stroke: '#587f99', strokeWidth: 2 },
      { path: 'M19.5 3.8h-3.7M4.5 20.2h3.7', stroke: '#587f99', strokeWidth: 2 },
    ],
  },
  gated: {
    layers: [
      { path: 'M7.25 10V7.5a4.75 4.75 0 0 1 9.5 0V10', stroke: '#8a6827', strokeWidth: 2 },
      { path: 'M5 9.5h14v11.75H5z', fill: '#e2bd58', stroke: '#9a762f', strokeWidth: 1.5 },
      { path: 'M12 13.25a1.5 1.5 0 0 1 .75 2.8V18h-1.5v-1.95a1.5 1.5 0 0 1 .75-2.8z', fill: '#76551c' },
    ],
  },
};

export function iconForTagBadge(tag: string): TagBadgeIcon | null {
  return TAG_ICONS[tag] ?? null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? value as Record<string, unknown> : null;
}

function normalizeTags(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (tag): tag is string => typeof tag === 'string' && tag.trim().length > 0,
  );
}

export function resolveTagBadgeTags(value: unknown): string[] {
  const record = asRecord(value);
  if (!record) return [];
  const props = asRecord(record.props);
  const attrs = asRecord(record.attrs) ?? asRecord(props?.attrs);
  const candidates = [record.tags, props?.tags, attrs?.tags];
  for (const candidate of candidates) {
    const tags = normalizeTags(candidate);
    if (tags.length > 0) return tags;
  }
  return [];
}

export function hasTagBadgeDependencyRow(value: unknown): boolean {
  const record = asRecord(value);
  const props = asRecord(record?.props);
  const attrs = asRecord(record?.attrs) ?? asRecord(props?.attrs) ?? {};
  return ['thirdPartyDependencies', 'botDependencies', 'modelDependencies']
    .some((key) => Array.isArray(attrs[key]) && (attrs[key] as unknown[]).length > 0);
}

export function layoutTagBadgeChips(tags: string[]): TagChipPlacement[] {
  const visible = tags.slice(0, TAG_BADGE_MAX_VISIBLE);
  const overflow = tags.length - visible.length;
  const placements: TagChipPlacement[] = [];
  let cursorX = 0;

  for (const tag of visible) {
    const icon = iconForTagBadge(tag);
    const width =
      (icon ? TAG_BADGE_GLYPH_WIDTH + TAG_BADGE_GLYPH_GAP : 0) +
      tag.length * TAG_BADGE_CHAR_PX +
      TAG_BADGE_PAD_X * 2;
    placements.push({ text: tag, icon, x: cursorX, width, variant: 'tag' });
    cursorX += width + TAG_BADGE_GAP;
  }

  if (overflow > 0) {
    const text = `+${overflow}`;
    const width = text.length * TAG_BADGE_CHAR_PX + TAG_BADGE_PAD_X * 2;
    placements.push({ text, icon: null, x: cursorX, width, variant: 'overflow' });
  }

  return placements;
}

export function resolveTagBadgeLayout(
  value: unknown,
  options: { reserveDependencyRow?: boolean } = {},
): TagBadgeLayout | null {
  const tags = resolveTagBadgeTags(value);
  if (tags.length === 0) return null;
  const chips = layoutTagBadgeChips(tags);
  const last = chips[chips.length - 1];
  const dependencyOffset = options.reserveDependencyRow !== false && hasTagBadgeDependencyRow(value)
    ? TAG_BADGE_GRC_ROW_HEIGHT
    : 0;
  return {
    chips,
    width: last ? last.x + last.width : 0,
    height: TAG_BADGE_HEIGHT,
    offsetY: TAG_BADGE_SHAPE_GAP + dependencyOffset,
  };
}

function escapeSvgText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function formatSvgNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(3).replace(/\.?0+$/, '');
}

function renderTagIconSvg(tag: string): string {
  const icon = iconForTagBadge(tag);
  if (!icon) return '';
  const paths = icon.layers.map((layer) => {
    const fill = layer.fill ? ` fill="${layer.fill}"` : ' fill="none"';
    const stroke = layer.stroke ? ` stroke="${layer.stroke}"` : '';
    const strokeWidth = layer.stroke ? ` stroke-width="${formatSvgNumber(layer.strokeWidth ?? 1)}"` : '';
    const fillRule = layer.fillRule ? ` fill-rule="${layer.fillRule}"` : '';
    return `<path d="${layer.path}"${fill}${stroke}${strokeWidth}${fillRule} stroke-linecap="round" stroke-linejoin="round" />`;
  }).join('');
  return `<g data-process-tag-icon="${escapeSvgText(tag)}" transform="translate(${TAG_BADGE_PAD_X} 2) scale(0.5)">${paths}</g>`;
}

export function renderTagBadgesSvg(value: unknown, shapeHeight: number): string {
  const layout = resolveTagBadgeLayout(value);
  if (!layout) return '';
  const y = shapeHeight + layout.offsetY;
  const chips = layout.chips.map((chip) => {
    const labelX = chip.icon
      ? TAG_BADGE_PAD_X + TAG_BADGE_GLYPH_WIDTH + TAG_BADGE_GLYPH_GAP
      : TAG_BADGE_PAD_X;
    const fill = chip.variant === 'overflow' ? '#e2e8f0' : '#f1f5f9';
    const stroke = chip.variant === 'overflow' ? '#94a3b8' : '#cbd5e1';
    const icon = chip.icon ? renderTagIconSvg(chip.text) : '';
    return `<g transform="translate(${formatSvgNumber(chip.x)} 0)">` +
      `<rect width="${formatSvgNumber(chip.width)}" height="${TAG_BADGE_HEIGHT}" rx="8" ry="8" fill="${fill}" stroke="${stroke}" stroke-width="1" />` +
      icon +
      `<text x="${formatSvgNumber(labelX)}" y="11" font-size="10" font-family="Inter, Arial, sans-serif" fill="#475569"${chip.variant === 'overflow' ? ' font-weight="700"' : ''}>${escapeSvgText(chip.text)}</text>` +
      `</g>`;
  }).join('');
  return `<g data-process-tag-badges="true" transform="translate(0 ${formatSvgNumber(y)})">${chips}</g>`;
}

export function resolveTagBadgeOverflow(
  value: unknown,
  shapeWidth: number,
): { right: number; bottom: number } | null {
  const layout = resolveTagBadgeLayout(value);
  if (!layout) return null;
  return {
    right: Math.max(0, layout.width - shapeWidth),
    bottom: layout.offsetY + layout.height,
  };
}
